import cs from "classnames";
import React from "react";
import { FaSort } from "react-icons/fa";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useOnClickOutside } from "src/common/hooks/useOnClickOutside";
import { useAuthState } from "src/features/auth";
import { useOrgModParams } from "src/features/orgmod";
import { modMessages, orgMessages } from "src/messages/generic";

import "./OrgMod.css";

type OrgKey = keyof typeof orgMessages;
type ModKey = keyof typeof modMessages;

interface OrgModValue {
	org: string | undefined;
	mod: string | undefined;
}

interface Org {
	value: string;
	label: string;
	modules: Mod[];
}

interface Mod {
	value: string;
	label: string;
}

export const OrgMod: React.FunctionComponent = () => {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const menuRef = React.useRef<HTMLDivElement>(null);

	const { isOpen, toggle, close } = useToggleClose();
	useOnClickOutside(containerRef, close);

	React.useEffect(() => {
		if (!isOpen) {
			return;
		}
		const { current: menuEl } = menuRef;
		if (menuEl === null) {
			return;
		}
		const rect = menuEl.getBoundingClientRect();
		menuEl.style.maxHeight = `${window.innerHeight - rect.top - 10}px`;
	}, [isOpen]);

	const orgs = useOrgs();
	const value = useOrgModParams();

	const org = useSelectedOrg(orgs, value);
	const mod = useSelectedMod(org, value);

	const onChangeValue = useSelectOrgMod();

	if (org === undefined || mod === undefined) {
		return null;
	}

	if (orgs.length === 1) {
		return (
			<div className="orgmod">
				<div className="orgmod__value">
					<div className="orgmod__org" title={org.label}>
						{org.label}
					</div>
					<div className="orgmod__mod" title={mod.label}>
						{mod.label}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			ref={containerRef}
			className={cs("orgmod", { "orgmod--open": isOpen })}
		>
			<div className="orgmod__button" onClick={toggle}>
				<div className="orgmod__value">
					<div className="orgmod__org" title={org.label}>
						{org.label}
					</div>
					<div className="orgmod__mod" title={mod.label}>
						{mod.label}
					</div>
				</div>
				<div className="orgmod__toggle">
					<FaSort />
				</div>
			</div>
			<div ref={menuRef} className="orgmod__menu">
				{orgs.map(org => (
					<div key={org.value} className="orgmod__item">
						<div className="orgmod__org">{org.label}</div>
						{org.modules.map(mod => (
							<div
								key={mod.value}
								className="orgmod__mod"
								onClick={() => {
									onChangeValue({
										org: org.value,
										mod: mod.value
									});
									close();
								}}
							>
								{mod.label}
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	);
};

function useToggleClose(): { isOpen: boolean; toggle(): void; close(): void } {
	const [isOpen, setOpen] = React.useState(false);
	const toggle = React.useCallback(() => {
		setOpen(isOpen => !isOpen);
	}, []);
	const close = React.useCallback(() => {
		setOpen(false);
	}, []);
	return {
		isOpen,
		toggle,
		close
	};
}

function useOrgs(): Org[] {
	const { user } = useAuthState();
	const { formatMessage } = useIntl();
	return React.useMemo(() => {
		if (user === null) {
			return [];
		}
		const map = new Map<OrgKey, Set<ModKey>>();
		for (const { organization: org, module: mod } of user.permissions) {
			if (!isOrgKey(org)) {
				throw new Error(`organization not found: ${org}`);
			}
			if (!isModKey(mod)) {
				throw new Error(`module not found: ${mod}`);
			}
			const set = map.get(org);
			if (set === undefined) {
				map.set(org, new Set<ModKey>([mod]));
			} else {
				set.add(mod);
			}
		}
		const opts: Org[] = [];
		for (const [org, mods] of map.entries()) {
			const opt: Org = {
				value: org,
				label: formatMessage(orgMessages[org]),
				modules: []
			};
			opts.push(opt);
			for (const mod of mods) {
				opt.modules.push({
					value: mod,
					label: formatMessage(modMessages[mod])
				});
			}
		}
		opts.sort(compareByLabel);
		for (const opt of opts) {
			opt.modules.sort(compareByLabel);
		}
		return opts;
	}, [user, formatMessage]);
}

function useSelectedOrg(
	orgs: Org[],
	value: OrgModValue | undefined
): Org | undefined {
	return React.useMemo(
		() =>
			value === undefined || value.org === undefined
				? undefined
				: orgs.find(org => org.value === value.org),
		[orgs, value]
	);
}

function useSelectedMod(
	org: Org | undefined,
	value: OrgModValue | undefined
): Mod | undefined {
	return React.useMemo(
		() =>
			value === undefined || value.mod === undefined || org === undefined
				? undefined
				: org.modules.find(mod => mod.value === value.mod),
		[org, value]
	);
}

function useSelectOrgMod(): ({ org, mod }: OrgModValue) => void {
	const navigate = useNavigate();
	return React.useCallback(
		({ org, mod }) => {
			navigate(`/${org}/${mod}/patients/list`);
		},
		[navigate]
	);
}

function isOrgKey(key: string): key is OrgKey {
	return key in orgMessages;
}

function isModKey(key: string): key is ModKey {
	return key in modMessages;
}

function compareByLabel<T extends { label: string }>(a: T, b: T): -1 | 0 | 1 {
	if (a.label < b.label) {
		return -1;
	}
	if (a.label > b.label) {
		return 1;
	}
	return 0;
}
